import React, { FC } from 'react';
import Link from 'next/link';
import FavouriteButton from '#components/controls/buttons/favouriteButton/FavouriteButton';
import s from '#components/teasers/special-teaser/SpecialTeaser.module.scss';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { ITeaserRenderer } from '#components/lists/horizontal-scroll-list/HorizontalScrollList';
import getRoute from '#utils/getRoute';
import ProgressBar from '#components/feedback/progress-bar/ProgressBar';
import TeaserTitle from '#components/common/teaser-meta/TeaserTitle';
import TeaserDescription from '#components/common/teaser-meta/TeaserDescription';
import EpisodeInfos from '#components/common/teaser-meta/EpisodeInfos';
import TeaserTextBox from '#components/common/teaser-meta/TeaserTextBox';
import { useOvermindState } from '#store/overmind';
import { Media } from '#types/generated/graphql-types';

interface Props extends ITeaserRenderer {
  teaserDataRaw: Media;
  disableLikeButton?: boolean;
  onClick?: (event: React.MouseEvent) => void;
  disableLinkRedirection?: boolean;
}

const SpecialTeaser: FC<Props> = ({
  teaserDataRaw,
  continueWatchingState,
  disableLikeButton,
  onClick,
  disableLinkRedirection = false,
}) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { isLoggedIn } = useOvermindState().user;

  const handleClick = (event) => {
    if (onClick) {
      onClick(event);
      return;
    }
    router.push(getRoute(teaserDataRaw, t));
  };

  const teaserFooter = () => (
    <>
      {!!teaserDataRaw?.series?.title && (
        <>
          <TeaserTitle lines={1} size="small" bottomGutter={false}>
            {teaserDataRaw?.series?.title || ''}
          </TeaserTitle>
          <TeaserTitle size="medium" element="p">
            <EpisodeInfos data={teaserDataRaw} />
            {teaserDataRaw.title}
          </TeaserTitle>
        </>
      )}
      {!teaserDataRaw?.series?.title && (
        <>
          {!!teaserDataRaw?.categories?.length && (
            <TeaserTitle lines={1} size="small" bottomGutter={false}>
              {teaserDataRaw.categories
                .map((category) => category.title)
                .join(' | ')}
            </TeaserTitle>
          )}
          <TeaserTitle size="medium">{teaserDataRaw.title}</TeaserTitle>
        </>
      )}
      <TeaserDescription>{teaserDataRaw.teaserDescription}</TeaserDescription>
    </>
  );

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions
    <article className={s.host} onClick={handleClick}>
      <div className={s.media}>
        <div className="tw-aspect-w-16 tw-aspect-h-9">
          {!!teaserDataRaw?.teaserImageFormats?.sixteenByNine?.medium && (
            <img
              data-host="mediabox"
              src={teaserDataRaw?.teaserImageFormats?.sixteenByNine?.medium}
              alt={teaserDataRaw.title}
            />
          )}
          {teaserDataRaw?.teaserImage?.['16_9']?.srcSet?.length > 0 && (
            <img
              data-host="mediabox"
              srcSet={teaserDataRaw?.teaserImage?.['16_9']?.srcSet}
              alt={teaserDataRaw.title}
            />
          )}
          {!teaserDataRaw?.teaserImage?.['16_9']?.srcSet?.length &&
            !teaserDataRaw?.teaserImageFormats?.sixteenByNine?.medium && (
              <img
                data-host="mediabox"
                src="/static/placeholder/16-9.jpg"
                alt={teaserDataRaw.title}
              />
            )}
        </div>
        {continueWatchingState && (
          <div className={s.progressbar}>
            <ProgressBar
              total={teaserDataRaw.mediaDuration}
              value={continueWatchingState.currentTime}
            />
          </div>
        )}
        {!!teaserDataRaw.iri && !disableLikeButton && isLoggedIn && (
          <div className={s.buttonAdd}>
            <FavouriteButton item={teaserDataRaw} isSizeSmall />
          </div>
        )}
      </div>

      <TeaserTextBox transparent={true}>
        {disableLinkRedirection ? (
          teaserFooter()
        ) : (
          <Link href={getRoute(teaserDataRaw, t)} legacyBehavior>
            <a className={s.link}>{teaserFooter()}</a>
          </Link>
        )}
      </TeaserTextBox>
    </article>
  );
};

export default SpecialTeaser;
